import {Component, inject, input, OnChanges, OnInit, output, SimpleChanges, WritableSignal} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {IconCloseSquareComponent} from "@components/icons/icon-close-square/icon-close-square.component";
import {IconDocumentDownloadComponent} from "@components/icons/icon-document-download/icon-document-download.component";
import {IconExportarExcelComponent} from "@components/icons/icon-exportar-excel/icon-exportar-excel.component";
import {IconImportComponent} from "@components/icons/icon-import/icon-import.component";
import {IconTrashComponent} from "@components/icons/icon-trash/icon-trash.component";
import {TIPO_ARQUIVO_OPTIONS} from "@core/enums/tipo-arquivo.enum";
import {ValidationErrorImport} from '@core/models/validation-errors-imports.model';
import {ArquivoService} from "@core/services/arquivo.service";
import {MessageService, PrimeTemplate} from "primeng/api";
import {Button} from "primeng/button";
import {Card} from "primeng/card";
import {Dialog} from "primeng/dialog";
import {FileSelectEvent, FileUpload} from "primeng/fileupload";
import {Fluid} from "primeng/fluid";
import {Select} from "primeng/select";
import {TableModule} from "primeng/table";
import {Tooltip} from "primeng/tooltip";
import {ProgressSpinner} from 'primeng/progressspinner';

@Component({
  selector: 'app-importar-arquivo',
  imports: [
    Button,
    Dialog,
    FileUpload,
    Fluid,
    IconCloseSquareComponent,
    IconDocumentDownloadComponent,
    IconImportComponent,
    PrimeTemplate,
    ReactiveFormsModule,
    Select,
    IconExportarExcelComponent,
    Tooltip,
    IconTrashComponent,
    TableModule,
    Card,
    ProgressSpinner
  ],
  templateUrl: './importar-arquivo.html',
  styleUrl: './importar-arquivo.css'
})
export class ImportarArquivo implements OnInit, OnChanges {

  visible = input(false);
  visibleChange = output<boolean>();
  uploadedFilesDate: Date | null = null;
  uploadedFiles: File[] = [];
  importForm!: FormGroup;
  disabledButtonBaixarModelo: boolean = false;
  showFailImportDialog: boolean | WritableSignal<boolean> = false;
  validationErrorImport: ValidationErrorImport[] = [];
  genericErrorMessage: string | null = null;
  importCompleted = output<void>();
  protected readonly tipoArquivoOptions = TIPO_ARQUIVO_OPTIONS;
  protected readonly maxFileSize = 10485760; // 10MB em bytes
  protected readonly allowedExtensions = ['xlsx'];
  protected fileTooLarge = false;
  protected fileInvalidExtension = false;
  protected loading: boolean = false;
  private fb = inject(FormBuilder);
  private service = inject(ArquivoService);
  private messageService = inject(MessageService);

  ngOnInit() {
    this.initImportForm();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible']) {
      this.reset();
    }
  }

  initImportForm() {
    this.importForm = this.fb.group({
      tipoArquivo: [{value: 1, disabled: true}, Validators.required], // value = 1 refere-se a TIPO_ARQUIVO_OPTIONS.Excel
      arquivo: [null, Validators.required],
    });
  }

  onSelectFileUpload(event: FileSelectEvent) {
    const file = event.currentFiles[0];
    if (file && file.size > this.maxFileSize) {
      this.fileTooLarge = true;
      this.uploadedFiles = [];
      return;
    }
    this.fileTooLarge = false;
    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!this.allowedExtensions.includes(extension)) {
      this.fileInvalidExtension = true;
      this.uploadedFiles = [];
      return;
    }
    this.uploadedFiles = event.currentFiles;
    this.uploadedFilesDate = new Date();
    this.disabledButtonBaixarModelo = false;
  }

  getFormattedMaxSize(): string {
    const bytes = this.maxFileSize;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  }

  reset() {
    this.initImportForm();
    this.uploadedFiles = [];
    this.uploadedFilesDate = null;
    this.disabledButtonBaixarModelo = false;
    this.showFailImportDialog = false;
    this.fileInvalidExtension = false;
    this.fileTooLarge = false;
  }

  closeImportDialog() {
    this.reset();
    this.visibleChange.emit(false);
  }

  closeFailImportDialog() {
    this.showFailImportDialog = false;
  }

  downloadModelo(): void {
    this.service.downloadModelo().subscribe((res) => {
      const blob = new Blob([res.body!], {
        type: res.body?.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const contentDisposition = res.headers.get('content-disposition');
      let fileName = 'modelo.xlsx';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) {
          fileName = match[1];
        }
      }

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);

      this.disabledButtonBaixarModelo = true;
      this.messageService.add({
        severity: 'success',
        summary: 'Successo!',
        detail: 'Modelo foi baixado.'
      });
    });
  }

  onRemoveFile(index: number) {
    this.uploadedFilesDate = null;
    this.uploadedFiles.splice(index, 1);
  }

  import() {
    if (!this.uploadedFiles || this.uploadedFiles.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Atenção!',
        detail: 'Selecione um arquivo antes de importar.'
      });
      return;
    }
    this.loading = true;
    const file = this.uploadedFiles[0];
    this.service.import(file).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Sucesso!',
          detail: 'Arquivo foi importado.'
        });
        this.loading = false;
        this.closeImportDialog();
        this.importCompleted.emit();
      },
      error: (err) => {
        console.error('Erro no upload', err);
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Erro!',
          detail: 'Importação falhou.'
        });

        // resetar variáveis antes de preencher
        this.validationErrorImport = [];
        this.genericErrorMessage = null;

        // 422 - lista de erros de validação
        if (err.status === 422 && Array.isArray(err.error?.errors)) {
          this.validationErrorImport = err.error.errors as ValidationErrorImport[];

          // 400 - apenas mensagem genérica
        } else if (err.status === 400 && err.error?.message) {
          this.genericErrorMessage = err.error.message;

        } else {
          this.genericErrorMessage = 'Erro inesperado durante a importação.';
        }

        this.showFailImportDialog = true;
      }
    });
  }

}
